import { _MiscMessages, ConfigSystemParams, DashboardProfit } from 'src/app/core/consts';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { catchError, combineLatest, debounceTime, distinctUntilChanged, Observable, of, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { DashboardService } from 'src/app/core/services/utelize/general/dashboard-service';
import { DashboardStatistic } from 'src/app/core/interfaces/general/dashboard-statistic';
import { DashboardTotal_Previous_Current_PercentDTO_EmptyObject } from 'src/app/core/models/system/dashboard/dashboard-total-previous-current-percent.dto';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { OurDate } from 'src/app/core/interfaces/general/our-date';
import { populateOurDate } from 'src/app/core/functions-generic/functions-generic-date';
import { Router } from '@angular/router';
import { TippyToolTipService } from 'src/app/core/services/utelize/general/tippy-tool-tip-service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-widget-row-alerts',
  templateUrl: './widget-row-alerts.component.html',
  styleUrls: ['./widget-row-alerts.component.scss'],
  providers: [TippyToolTipService]
})

export class WidgetRowAlertsComponent implements AfterViewInit, OnDestroy, OnInit {
  
  @Input() allowContainerClose: boolean | undefined;
  @Input() containerTitle     : string  | undefined;
  @Input() marginBottom       : string  | undefined="mb-0";
  @Input() useContainer       : boolean | undefined;
  @Input() showTitleHintText  : boolean | undefined;
    
  @Output() containerClosed: EventEmitter<void>=new EventEmitter<void>();
    
  get pollingWidgets() { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.WidgetRow_Polling_Alerts, false); }
    
  get maxWidgetsPerRow() { return this.configSystemService.getValueAsInteger(ConfigSystemParams.WidgetRow_MaxWidgetsPerRow_Alerts, 4); }

  private filterCompanyID                     : number | undefined;
  private filterDate                          : OurDate | undefined=populateOurDate(this.exceptionsService, Date.now());
  private statTitleAlertsTimesTriggeredCountry: string="Country alerts";
  private statTitleAlertsTimesTriggeredUsage  : string="Usage alerts";
  private statTitleAlertsTotalLimitIncreases  : string="Limit increase alerts";
  private statTitleAlertsTotalThrottles       : string="Throttle alerts";
  
  private destroy$  =new Subject<void>();
  private selection$=new Subject<{ companyId: number; date: { year: number; month: number; day: number } }>();
  
  public expandContainer : boolean=true;
  public statData!       : Array<DashboardStatistic>;
  public visibleContainer: boolean=true;

  constructor(
    private changeDetectorRef  : ChangeDetectorRef,
    private configSystemService: ConfigSystemService,
    private dashboardService   : DashboardService,
    private exceptionsService  : ExceptionsService,
    private router             : Router,
    private tippyToolTipService: TippyToolTipService
  ) {

  }

  public navigateToTriggeredAlerts(): void {
    try {
      this.router.navigate(['/system-admin/triggered-alerts']);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnInit(): void {
    try {
      this.initSelectionStream();
      
      if (!this.containerTitle || this.containerTitle.trim()=="")
        this.containerTitle=_MiscMessages.TitleNotSet;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public ngAfterViewInit(): void {
    try {
      setTimeout(() => {
        this.displayData();
      });
      
      this.tippyToolTipService.setupBootstrapTooltips('[data-bs-toggle="tooltip"]', 'left');
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public ngOnDestroy(): void {
    try {
      this.destroy$.next();
      this.destroy$.complete();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private displayData() {
    try {
      this.statData=[];
                   
      let statAlertsTimesTriggeredUsage: DashboardStatistic={
        decimalPlaces : 0,
        icon          : "bx-error",
        iconColour    : "danger",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : <string><unknown>"",
        profit        : "",
        profitUpIsGood: true,
        showProfit    : true,
        showSpinner   : false,
        title         : this.statTitleAlertsTimesTriggeredUsage,
        value         : undefined,
      }
                   
      let statAlertsTimesTriggeredCountry: DashboardStatistic={
        decimalPlaces : 0,
        icon          : "bx-error",
        iconColour    : "danger",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : <string><unknown>"",
        profit        : "",
        profitUpIsGood: true,
        showProfit    : true,
        showSpinner   : false,
        title         : this.statTitleAlertsTimesTriggeredCountry,
        value         : undefined,
      }
                   
      let statAlertsTotalLimitIncreases: DashboardStatistic={
        decimalPlaces : 0,
        icon          : "bx-error-circle",
        iconColour    : "danger",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : <string><unknown>"",
        profit        : "",
        profitUpIsGood: true,
        showProfit    : true,
        showSpinner   : false,
        title         : this.statTitleAlertsTotalLimitIncreases,
        value         : undefined,
      }
                   
      let statAlertsTotalThrottles: DashboardStatistic={
        decimalPlaces : 0,
        icon          : "bx-error-circle",
        iconColour    : "danger",
        link          : undefined,
        linkText      : undefined,
        loading       : true,
        percentage    : <string><unknown>"",
        profit        : "",
        profitUpIsGood: true,
        showProfit    : true,
        showSpinner   : false,
        title         : this.statTitleAlertsTotalThrottles,
        value         : undefined,
      }
                
      this.statData.push(statAlertsTimesTriggeredUsage);
      this.statData.push(statAlertsTimesTriggeredCountry);
      this.statData.push(statAlertsTotalLimitIncreases);
      this.statData.push(statAlertsTotalThrottles);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
       return (undefined);
    }
  }
  
  private initSelectionStream(): void {
    try {
      this.selection$
      .pipe(
        debounceTime(150),
        distinctUntilChanged((a, b) =>
          a.companyId ===b.companyId  &&
          a.date.year ===b.date.year  &&
          a.date.month===b.date.month &&
          a.date.day  ===b.date.day
        ),
        switchMap(selected => {
          const companyId=selected.companyId;
          const year     =selected.date.year;
          const month    =selected.date.month;
          const day      =selected.date.day;
          
          const safeStart=<T>(observable: Observable<T>, fallback: T) =>
            observable.pipe(
              startWith(fallback),
              catchError(err => {
                this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(err)));
                return of(fallback);
              })
            );
          
          const alertsTimesTriggeredCountrySource=this.pollingWidgets
                                                  ? this.dashboardService.getAlertsTimesTriggeredCountryUpdates(companyId, year, month, day)
                                                  : this.dashboardService.getAlertsTimesTriggeredCountry       (companyId, year, month, day);
          
          const alertsTimesTriggeredUsageSource=this.pollingWidgets
                                                ? this.dashboardService.getAlertsTimesTriggeredUsageUpdates(companyId, year, month, day)
                                                : this.dashboardService.getAlertsTimesTriggeredUsage       (companyId, year, month, day);
          
          const alertsTotalLimitIncreasesSource=this.pollingWidgets
                                                ? this.dashboardService.getAlertsTotalLimitIncreasesUpdates(companyId, year, month, day)
                                                : this.dashboardService.getAlertsTotalLimitIncreases       (companyId, year, month, day);
          
          const alertsTotalThrottlesSource=this.pollingWidgets
                                           ? this.dashboardService.getAlertsTotalThrottlesUpdates(companyId, year, month, day)
                                           : this.dashboardService.getAlertsTotalThrottles       (companyId, year, month, day);

          return combineLatest({
            alertsTimesTriggeredCountry: safeStart(alertsTimesTriggeredCountrySource, DashboardTotal_Previous_Current_PercentDTO_EmptyObject()),
            alertsTimesTriggeredUsage  : safeStart(alertsTimesTriggeredUsageSource,   DashboardTotal_Previous_Current_PercentDTO_EmptyObject()),
            alertsTotalLimitIncreases  : safeStart(alertsTotalLimitIncreasesSource,   DashboardTotal_Previous_Current_PercentDTO_EmptyObject()),
            alertsTotalThrottles       : safeStart(alertsTotalThrottlesSource,        DashboardTotal_Previous_Current_PercentDTO_EmptyObject())
          });
          // .pipe(
          //   catchError((error) => {
          //     this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(error)));
          //     return EMPTY; //👈 CRN_20251111 - If one fails, nothing is returned
          //   })
          // );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        this.applyAlertsTimesTriggeredCountry(result.alertsTimesTriggeredCountry);
        this.applyAlertsTimesTriggeredUsage  (result.alertsTimesTriggeredUsage);
        this.applyAlertsTotalLimitIncreases  (result.alertsTotalLimitIncreases);
        this.applyAlertsTotalThrottles       (result.alertsTotalThrottles);

        this.changeDetectorRef.markForCheck();
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
    
  public emitSelection(filterCompanyID: number | undefined, filterDate: OurDate | undefined): void {
    try {
      this.filterCompanyID=filterCompanyID;
      this.filterDate     =filterDate;

      const companyId=this.filterCompanyID ?? -1;
      const date     ={
        year : <number>(this.filterDate?.year  ?? 1),
        month: <number>(this.filterDate?.month ?? 1),
        day  : <number>(this.filterDate?.day   ?? 1),
      };
      
      this.selection$.next({ companyId, date });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyAlertsTimesTriggeredCountry(data: { percentageChange: number; currentTotal: number }) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleAlertsTimesTriggeredCountry) {
          stat.loading   =false,
          stat.percentage=<string><unknown>data.percentageChange,
          stat.profit    =data.percentageChange==0 ? DashboardProfit.Equal : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up),
          stat.value     =data.currentTotal;

          stat.iconColour=(Number(data.currentTotal) || 0)>0 ? "danger" : "success";
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyAlertsTimesTriggeredUsage(data: { percentageChange: number; currentTotal: number }) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleAlertsTimesTriggeredUsage) {
          stat.loading   =false,
          stat.percentage=<string><unknown>data.percentageChange,
          stat.profit    =data.percentageChange==0 ? DashboardProfit.Equal : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up),
          stat.value     =data.currentTotal;

          stat.iconColour=(Number(data.currentTotal) || 0)>0 ? "danger" : "success";
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyAlertsTotalLimitIncreases(data: { percentageChange: number; currentTotal: number }) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleAlertsTotalLimitIncreases) {
          stat.loading   =false,
          stat.percentage=<string><unknown>data.percentageChange,
          stat.profit    =data.percentageChange==0 ? DashboardProfit.Equal : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up),
          stat.value     =data.currentTotal;

          stat.iconColour=(Number(data.currentTotal) || 0)>0 ? "danger" : "success";
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyAlertsTotalThrottles(data: { percentageChange: number; currentTotal: number }) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: DashboardStatistic) => {
        if (stat.title==this.statTitleAlertsTotalThrottles) {
          stat.loading   =false,
          stat.percentage=<string><unknown>data.percentageChange,
          stat.profit    =data.percentageChange==0 ? DashboardProfit.Equal : (data.percentageChange<0 ? DashboardProfit.Down : DashboardProfit.Up),
          stat.value     =data.currentTotal;

          stat.iconColour=(Number(data.currentTotal) || 0)>0 ? "danger" : "success";
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public trendPercent(stat: DashboardStatistic): number | null {
    try {
      if (!stat?.showProfit || stat?.percentage==null || stat.percentage=="")
        return null;

      const percent=Number(stat.percentage);

      return Number.isFinite(percent) ? percent : null;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return null;
    }
  }

  public toggleContainerExpansion(): void {
    try {
      this.expandContainer=!this.expandContainer;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public collapseContainer(): void {
    try {
      this.expandContainer=false;
      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public closeContainer(): void {
    try {
      this.visibleContainer=false;
      this.containerClosed.emit();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public restoreContainer(): void {
    try {
      this.visibleContainer=true;
      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
